// src/preview/stage.ts
// The browser draft renderer: loads everything a spec needs (library files,
// text cards, Kindle pages, music) and draws the frame at any time t onto a
// canvas, the way make_video's ffmpeg graph composes it:
//   background (zoom, dim) → background overlays → beat images → text cards
//   → end card → end text.
// Player.tsx drives it (playback, scrubbing, recording a draft).
//
// Media and cards are cached at module level, so rebuilding the stage after
// each edit only loads what changed.
// ─────────────────────────────────────────────────────────────────

import { DEFAULTS, backgroundOverlays, deepMerge, type Json } from './spec';
import { BUILTIN_IMAGES, parseBuiltinRef } from '@ring/shared/projects';
import { fetchInChunks } from '../rangedFetch';
import { cardTokens, kindleSize, kindleTokens, rasterise, type Card, type Tokens } from './cards';
import {
  alphaAt, backgroundSourceRect, coverRect, endImageRect, endText, endWindow, imageItems, imageRect,
  overlayRect, overlayWindow, stageSize, textItems, timeline, type Rect, type Timeline, type Window,
} from './timeline';

/** What a spec file field points at, as the stage needs it. */
export interface StageAsset {
  url:      string;
  kind:     'image' | 'music' | 'video';
  filename: string;
  /** The detected type, e.g. image/jpeg (some ffmpeg filters behave differently by format). */
  contentType?: string;
}

/**
 * Looks up a file field's value (`asset:<id>`, `""`, or anything else).
 * Returns the asset, or a message saying why it can't be used.
 */
export type AssetLookup = (value: unknown) => StageAsset | { missing: string };

export interface StageNote {
  /** error: the preview can't show this; approx: shown, but not exactly as the final render; warn: worth knowing. */
  level:   'error' | 'approx' | 'warn';
  message: string;
}

export interface StageAudio {
  buffer:  AudioBuffer;
  /** Seconds into the track. */
  start:   number;
  fadeIn:  number;
  fadeOut: number;
}

type Drawable = HTMLCanvasElement | HTMLImageElement | HTMLVideoElement;

interface Placed { source: Drawable; rect: Rect; window: Window; opacity?: number }

// ── caches ───────────────────────────────────────────────────────

const imageCache = new Map<string, Promise<HTMLImageElement>>();
const audioCache = new Map<string, Promise<AudioBuffer>>();
const videoCache = new Map<string, HTMLVideoElement>();
/**
 * Cards by key, least recently used first. Bounded by pixels rather than
 * count: every edit of a text makes a new card, and a session of typing
 * would otherwise keep hundreds of stale ones. Cards are cropped to their
 * text (see rasterise), so the budget holds a lot of them.
 */
const cardCache = new Map<string, { card: Promise<Card>; pixels: number }>();
const MAX_CARD_PIXELS = 12_000_000; // about 48 MB of RGBA
let cardPixels = 0;

function cached<T>(cache: Map<string, Promise<T>>, key: string, make: () => Promise<T>): Promise<T> {
  let p = cache.get(key);
  if (!p) {
    p = make();
    p.catch(() => cache.delete(key)); // a failed load is retried next time
    cache.set(key, p);
  }
  return p;
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return cached(imageCache, url, async () => {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  });
}

let sharedAudioContext: AudioContext | null = null;

/** One AudioContext for decoding and playback (browsers limit how many a page may have). */
export function audioContext(): AudioContext {
  sharedAudioContext ??= new AudioContext();
  return sharedAudioContext;
}

function loadAudio(url: string): Promise<AudioBuffer> {
  return cached(audioCache, url, async () => {
    // In ranges: the API won't send a song over 8 MB in one response (api/src/assets.ts's MAX_WHOLE_MEDIA_BYTES)
    const { blob } = await fetchInChunks(url);
    return audioContext().decodeAudioData(await blob.arrayBuffer());
  });
}

function loadVideo(url: string): Promise<HTMLVideoElement> {
  let video = videoCache.get(url);
  if (!video) {
    video = document.createElement('video');
    video.muted = true;
    video.loop = true; // make_video loops a short background video (-stream_loop -1)
    video.playsInline = true;
    video.preload = 'auto';
    video.src = url;
    videoCache.set(url, video);
  }
  const v = video;
  if (v.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) return Promise.resolve(v);
  return new Promise((resolve, reject) => {
    const done = () => { v.removeEventListener('loadeddata', ok); v.removeEventListener('error', fail); };
    const ok = () => { done(); resolve(v); };
    const fail = () => { done(); videoCache.delete(url); reject(new Error('This video can\'t be played in the browser.')); };
    v.addEventListener('loadeddata', ok);
    v.addEventListener('error', fail);
  });
}

/** Seeks a (looping) background video to timeline time t and waits for the frame, giving up after a second. */
export function videoAt(video: HTMLVideoElement, t: number): Promise<void> {
  const target = video.duration > 0 ? t % video.duration : 0;
  if (Math.abs(video.currentTime - target) < 0.01) return Promise.resolve();
  return new Promise(resolve => {
    const done = () => { clearTimeout(timer); video.removeEventListener('seeked', done); resolve(); };
    const timer = setTimeout(done, 1000);
    video.addEventListener('seeked', done);
    video.currentTime = target;
  });
}

/** A text card or Kindle page, cached by everything that affects its pixels. */
function card(template: string, tokens: Tokens, w: number, h: number): Promise<Card> {
  const key = `${template}|${w}x${h}|${JSON.stringify(tokens)}`;
  const hit = cardCache.get(key);
  if (hit) {
    cardCache.delete(key); // move to the most recently used end
    cardCache.set(key, hit);
    return hit.card;
  }
  const entry = { card: limited(() => rasterise(template, tokens, w, h)), pixels: 0 };
  cardCache.set(key, entry);
  const drop = () => {
    if (cardCache.get(key) !== entry) return;
    cardCache.delete(key);
    cardPixels -= entry.pixels;
  };
  entry.card.then(c => {
    // Like make_video, a card drawn with fallback fonts (fonts failed to load) is never kept
    if (!c.fontsOk) return drop();
    if (cardCache.get(key) !== entry) return;
    entry.pixels = c.canvas.width * c.canvas.height;
    cardPixels += entry.pixels;
    for (const [k, e] of cardCache) {
      if (cardPixels <= MAX_CARD_PIXELS) break;
      if (e.pixels === 0) continue; // still rendering
      cardCache.delete(k);
      cardPixels -= e.pixels;
    }
  }, drop);
  return entry.card;
}

/** Card layouts in flight, across every stage build (each holds a hidden iframe while it runs). */
const MAX_LAYOUTS = 4;
let layouts = 0;
const waitingForLayout: (() => void)[] = [];

async function limited<T>(fn: () => Promise<T>): Promise<T> {
  while (layouts >= MAX_LAYOUTS) await new Promise<void>(resolve => waitingForLayout.push(resolve));
  layouts++;
  try {
    return await fn();
  } finally {
    layouts--;
    waitingForLayout.shift()?.();
  }
}

// ── drawing helpers ──────────────────────────────────────────────

function newCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = Math.max(1, Math.round(w));
  c.height = Math.max(1, Math.round(h));
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  return [c, ctx];
}

/** `src` resampled once to w x h (from source rect `from`, default all of it), so frames draw cheaply. */
function resampled(src: HTMLImageElement, w: number, h: number, from?: Rect): HTMLCanvasElement {
  const [c, ctx] = newCanvas(w, h);
  const r = from ?? { x: 0, y: 0, w: src.naturalWidth, h: src.naturalHeight };
  ctx.drawImage(src, r.x, r.y, r.w, r.h, 0, 0, c.width, c.height);
  return c;
}

const canvasFilter = (() => {
  try {
    const ctx = document.createElement('canvas').getContext('2d')!;
    ctx.filter = 'blur(2px)';
    return ctx.filter === 'blur(2px)';
  } catch {
    return false;
  }
})();

/** boxblur=40:4 as a gaussian: four passes of an 81px box filter. */
const PAD_SIGMA = Math.sqrt(4 * ((2 * 40 + 1) ** 2 - 1) / 12);

/** `big` (w x h plus an m-pixel margin all round) blurred by sigma, cropped back to w x h. */
function blurred(big: HTMLCanvasElement, w: number, h: number, m: number, sigma: number): HTMLCanvasElement {
  const [c, ctx] = newCanvas(w, h);
  if (canvasFilter) {
    ctx.filter = `blur(${sigma}px)`;
    ctx.drawImage(big, -m, -m);
    ctx.filter = 'none';
  } else {
    // No canvas filters (older Safari): shrink and stretch back up, which blurs about as much
    const k = sigma, [tiny, tctx] = newCanvas(Math.max(1, w / k), Math.max(1, h / k));
    tctx.drawImage(big, m, m, w, h, 0, 0, tiny.width, tiny.height);
    ctx.drawImage(tiny, 0, 0, w, h);
  }
  return c;
}

/**
 * make_video's blurred pad behind a fullscreen `contain` end card: the
 * cover-cropped image, boxblur=40:4, then darkened 35%. Edges are clamped
 * rather than fading to transparent. Close, not exact: ffmpeg's pad comes
 * out a little greyer at the edges (the parity fixture allows for it).
 *
 * A JPEG reaches boxblur as yuv420p, whose colour planes are half size, so
 * the same radius blurs colour twice as far as brightness (the pad comes
 * out greyer). `chromaTwice` copies that: brightness from one blur, colour
 * from a blur twice as wide.
 */
function blurredPad(src: HTMLImageElement, w: number, h: number, chromaTwice: boolean): HTMLCanvasElement {
  const m = Math.ceil(PAD_SIGMA * (chromaTwice ? 2 : 1) * 3);
  const r = coverRect(src.naturalWidth, src.naturalHeight, w, h);
  const [big, bctx] = newCanvas(w + 2 * m, h + 2 * m);
  // the picture in the middle, its edge rows and columns stretched into the margin
  bctx.drawImage(src, r.x, r.y, r.w, r.h, m, m, w, h);
  const sy = r.h / h; // one destination row, in source px
  bctx.drawImage(src, r.x, r.y, r.w, sy, m, 0, w, m);
  bctx.drawImage(src, r.x, r.y + r.h - sy, r.w, sy, m, h + m, w, m);
  bctx.drawImage(big, m, 0, 1, h + 2 * m, 0, 0, m, h + 2 * m);
  bctx.drawImage(big, w + m - 1, 0, 1, h + 2 * m, w + m, 0, m, h + 2 * m);
  const c = blurred(big, w, h, m, PAD_SIGMA);
  const ctx = c.getContext('2d')!;
  if (chromaTwice) {
    // Keep the colour of the wider blur and the brightness (bt601 luma, as JPEGs use) of the narrower:
    // shifting R, G and B by the same amount changes luma by that amount and leaves Cb/Cr alone
    const luma = ctx.getImageData(0, 0, w, h);
    const colour = blurred(big, w, h, m, PAD_SIGMA * 2).getContext('2d')!.getImageData(0, 0, w, h);
    const L = luma.data, C = colour.data;
    for (let i = 0; i < L.length; i += 4) {
      const dy = (0.299 * L[i] + 0.587 * L[i + 1] + 0.114 * L[i + 2])
        - (0.299 * C[i] + 0.587 * C[i + 1] + 0.114 * C[i + 2]);
      L[i] = C[i] + dy;
      L[i + 1] = C[i + 1] + dy;
      L[i + 2] = C[i + 2] + dy;
    }
    ctx.putImageData(luma, 0, 0);
  }
  ctx.fillStyle = 'rgba(0,0,0,0.35)';
  ctx.fillRect(0, 0, w, h);
  return c;
}

function isJpeg(a: StageAsset): boolean {
  return a.contentType ? a.contentType === 'image/jpeg' : /\.(jpe?g|jfif)$/i.test(a.filename);
}

function padColour(pad: string): string {
  return /^[0-9a-f]{6}$/i.test(pad) ? `#${pad}` : pad;
}

// ── the stage ────────────────────────────────────────────────────

export class StageError extends Error {}

export class Stage {
  readonly w: number;
  readonly h: number;
  readonly fps: number;
  readonly tl: Timeline;
  readonly notes: StageNote[] = [];
  audio: StageAudio | null = null;

  private bg: { source: HTMLCanvasElement | HTMLVideoElement; sw: number; sh: number } | null = null;
  private overlays: Placed[] = [];
  private images: Placed[] = [];
  private texts: Placed[] = [];
  private end: Placed | null = null;
  private endTextCard: Placed | null = null;

  readonly spec: Json;
  private readonly lookup: AssetLookup;

  private constructor(spec: Json, size: [number, number], lookup: AssetLookup) {
    this.spec = spec;
    this.lookup = lookup;
    [this.w, this.h] = size;
    this.fps = spec.fps;
    this.tl = timeline(spec);
  }

  get total(): number { return this.tl.total; }

  /**
   * Builds a stage for `rawSpec` at the canvas size make_video's tier would
   * use (`shrink` 2 = its --preview, half each side). Throws StageError
   * when the spec can't be previewed at all.
   */
  static async build(rawSpec: Json, lookup: AssetLookup, shrink = 2): Promise<Stage> {
    const spec = deepMerge(DEFAULTS, rawSpec);
    checkPlayable(spec);
    const stage = new Stage(spec, stageSize(spec.size, shrink), lookup);
    await stage.load();
    return stage;
  }

  private note(level: StageNote['level'], message: string) {
    this.notes.push({ level, message });
  }

  /** A library file for `label`, or null (with a note) if there isn't a usable one. */
  private asset(label: string, value: unknown, kinds: StageAsset['kind'][], required = false): StageAsset | null {
    if (value === undefined || value === '' || value === null) {
      if (required) this.note('error', `${label}: none chosen.`);
      return null;
    }
    const builtin = parseBuiltinRef(value);
    const a: StageAsset | { missing: string } = builtin
      ? { url: `/builtin/${BUILTIN_IMAGES[builtin].file}`, kind: 'image', filename: BUILTIN_IMAGES[builtin].label }
      : this.lookup(value);
    if ('missing' in a) {
      this.note('error', `${label}: ${a.missing}`);
      return null;
    }
    if (!kinds.includes(a.kind)) {
      this.note('error', `${label}: ${a.filename} is ${a.kind === 'music' ? 'music' : `a ${a.kind}`}, which can't be used here.`);
      return null;
    }
    return a;
  }

  private async load(): Promise<void> {
    const { spec, w, h, tl } = this;
    const tasks: Promise<void>[] = [];

    // Background: an image is pre-scaled to cover the frame (with headroom for the zoom), a video drawn live
    const bgAsset = this.asset('Background', spec.background?.file, ['image', 'video'], true);
    if (bgAsset) {
      tasks.push((async () => {
        try {
          if (bgAsset.kind === 'video') {
            const video = await loadVideo(bgAsset.url);
            this.bg = { source: video, sw: video.videoWidth, sh: video.videoHeight };
          } else {
            const img = await loadImage(bgAsset.url);
            const zoom = spec.background.zoom ?? {};
            const k = Math.min(3, Math.max(1, zoom.from ?? 1, zoom.to ?? 1.25));
            const src = resampled(img, w * k, h * k, coverRect(img.naturalWidth, img.naturalHeight, w, h));
            this.bg = { source: src, sw: src.width, sh: src.height };
          }
        } catch (err) {
          this.note('error', `Background: ${bgAsset.filename} couldn't be loaded (${errorText(err)}).`);
        }
      })());
    }

    // Background overlays (e.g. a Kindle Unlimited badge): pinned, not zoomed or dimmed
    const overlays = backgroundOverlays(spec);
    tasks.push(Promise.all(overlays.map(async (o, i) => {
      const a = this.asset(`Background overlay ${i + 1}`, o.file, ['image'], true);
      if (!a) return;
      try {
        const img = await loadImage(a.url);
        const rect = overlayRect(img.naturalWidth, img.naturalHeight, o, w, h);
        this.overlays[i] = { source: resampled(img, rect.w, rect.h), rect, window: overlayWindow(tl, o), opacity: o.opacity };
      } catch (err) {
        this.note('error', `Background overlay ${i + 1}: ${a.filename} couldn't be loaded (${errorText(err)}).`);
      }
    })).then(() => { this.overlays = this.overlays.filter(Boolean); }));

    // Text cards (card() limits how many are laid out at once)
    const texts = textItems(spec, tl);
    const template: string = spec.style.template;
    tasks.push(Promise.all(texts.map(async (item, i) => {
      const { tokens, clampedY } = cardTokens(item.box, spec, w, h);
      if (clampedY !== null) this.note('warn', `Beat ${item.beat + 1}: text position y=${item.box.y ?? spec.style.y} is in the area the app's buttons cover, so it's moved to ${clampedY}.`);
      try {
        const c = await card(template, tokens, w, h);
        if (!c.fontsOk) this.note('approx', 'The text fonts didn\'t load, so text uses fallback fonts.');
        this.texts[i] = { source: c.canvas, rect: c.rect, window: item.window };
      } catch (err) {
        this.note('error', `Beat ${item.beat + 1}: text couldn't be drawn (${errorText(err)}).`);
      }
    })).then(() => { this.texts = this.texts.filter(Boolean); }));

    // Beat images and Kindle pages
    const images = imageItems(spec, tl);
    tasks.push(Promise.all(images.map(async (item, i) => {
      const label = `Beat ${item.beat + 1} image`;
      try {
        if (item.image.kindle) {
          const [kw, kh] = kindleSize(item.image, w, h);
          const c = await card('kindle', kindleTokens(item.image.kindle, kw, kh), kw, kh);
          // Rendered at the size it's shown; c.rect is where its (cropped) pixels sit on the page
          const page = imageRect(kw, kh, item.image, w, h);
          this.images[i] = { source: c.canvas, rect: { ...c.rect, x: page.x + c.rect.x, y: page.y + c.rect.y }, window: item.window };
          return;
        }
        const a = this.asset(label, item.image.file, ['image'], true);
        if (!a) return;
        const img = await loadImage(a.url);
        const rect = imageRect(img.naturalWidth, img.naturalHeight, item.image, w, h);
        this.images[i] = { source: resampled(img, rect.w, rect.h), rect, window: item.window };
      } catch (err) {
        this.note('error', `${label} couldn't be drawn (${errorText(err)}).`);
      }
    })).then(() => { this.images = this.images.filter(Boolean); }));

    // End card, composed once into a single layer
    const end = spec.endCard;
    if (tl.hasEnd) {
      const a = this.asset('End card', end.image, ['image'], true);
      if (a) {
        tasks.push((async () => {
          try {
            const img = await loadImage(a.url);
            this.end = { ...this.endLayer(img, end, isJpeg(a)), window: endWindow(tl) };
          } catch (err) {
            this.note('error', `End card: ${a.filename} couldn't be loaded (${errorText(err)}).`);
          }
        })());
      }
      const et = endText(spec);
      if (et) {
        tasks.push((async () => {
          const { tokens } = cardTokens(et, spec, w, h);
          try {
            const c = await card(template, tokens, w, h);
            this.endTextCard = { source: c.canvas, rect: c.rect, window: endWindow(tl) };
          } catch (err) {
            this.note('error', `End card text couldn't be drawn (${errorText(err)}).`);
          }
        })());
      }
    }

    // Music
    const audio = spec.audio ?? {};
    const audioAsset = this.asset('Music', audio.file, ['music']);
    if (audioAsset) {
      tasks.push(loadAudio(audioAsset.url).then(
        buffer => { this.audio = { buffer, start: audio.start, fadeIn: audio.fadeIn, fadeOut: audio.fadeOut }; },
        err => this.note('error', `Music: ${audioAsset.filename} can't be played in this browser (${errorText(err)}).`),
      ));
      if (audio.loudnorm) this.note('approx', 'Volume levelling (loudnorm) is applied in the final render only.');
    }

    await Promise.all(tasks);
  }

  private endLayer(img: HTMLImageElement, end: Json, jpeg: boolean): { source: HTMLCanvasElement; rect: Rect } {
    const { w, h } = this;
    const full = { x: 0, y: 0, w, h };
    if (end.mode !== 'fullscreen') {
      const rect = endImageRect(img.naturalWidth, img.naturalHeight, end, w, h);
      return { source: resampled(img, rect.w, rect.h), rect };
    }
    if (end.fit === 'cover') {
      return { source: resampled(img, w, h, coverRect(img.naturalWidth, img.naturalHeight, w, h)), rect: full };
    }
    if (end.pad === 'none') {
      // the whole cover over the still-zooming background, like overlay mode at full size
      const rect = endImageRect(img.naturalWidth, img.naturalHeight, end, w, h);
      return { source: resampled(img, rect.w, rect.h), rect };
    }
    let base: HTMLCanvasElement, ctx: CanvasRenderingContext2D;
    if (end.pad === 'blur') {
      base = blurredPad(img, w, h, jpeg);
      ctx = base.getContext('2d')!;
      if (!canvasFilter) this.note('approx', 'This browser can\'t blur like the final render; the end card\'s blurred background is approximate.');
    } else {
      [base, ctx] = newCanvas(w, h);
      ctx.fillStyle = padColour(end.pad);
      ctx.fillRect(0, 0, w, h);
    }
    const r = endImageRect(img.naturalWidth, img.naturalHeight, end, w, h);
    ctx.drawImage(img, r.x, r.y, r.w, r.h);
    return { source: base, rect: full };
  }

  /** Draws the frame at `t` seconds. */
  draw(ctx: CanvasRenderingContext2D, t: number): void {
    const { w, h, spec } = this;
    ctx.globalAlpha = 1;
    ctx.imageSmoothingQuality = 'high';
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);

    if (this.bg) {
      const { source, sw, sh } = this.bg;
      if (sw > 0 && sh > 0) {
        const r = backgroundSourceRect(sw, sh, w, h, spec.background.zoom, this.total, t);
        ctx.drawImage(source, r.x, r.y, r.w, r.h, 0, 0, w, h);
      }
    }
    if (spec.style.dim > 0) {
      ctx.fillStyle = `rgba(0,0,0,${spec.style.dim})`; // like colorchannelmixer's rgb * (1 - dim)
      ctx.fillRect(0, 0, w, h);
    }
    for (const layer of [...this.overlays, ...this.images, ...this.texts, this.end, this.endTextCard]) {
      if (!layer) continue;
      const a = alphaAt(layer.window, t) * (layer.opacity ?? 1);
      if (a <= 0) continue;
      ctx.globalAlpha = a;
      ctx.drawImage(layer.source, layer.rect.x, layer.rect.y, layer.rect.w, layer.rect.h);
    }
    ctx.globalAlpha = 1;
  }

  /** The background video, if there is one, for Player to keep in step with the clock. */
  get video(): HTMLVideoElement | null {
    return this.bg?.source instanceof HTMLVideoElement ? this.bg.source : null;
  }
}

function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** The minimum a spec needs before it can be drawn at all; anything finer shows up as notes. */
function checkPlayable(spec: Json): void {
  const size = spec.size;
  if (!Array.isArray(size) || size.length !== 2 || !size.every(n => Number.isInteger(n) && n > 0 && n <= 4096)) {
    throw new StageError('size must be [width, height] in pixels.');
  }
  if (!Array.isArray(spec.beats) || spec.beats.length === 0) throw new StageError('Add at least one beat.');
  spec.beats.forEach((b: unknown, i: number) => {
    const d = (b as Json)?.duration;
    if (typeof d !== 'number' || !(d > 0)) throw new StageError(`Beat ${i + 1} needs a duration above 0.`);
  });
  if (spec.endCard?.image && !(typeof spec.endCard.duration === 'number' && spec.endCard.duration > 0)) {
    throw new StageError('The end card needs a duration above 0.');
  }
  if (!(typeof spec.style?.fade === 'number' && spec.style.fade >= 0)) throw new StageError('style.fade must be 0 or more.');
}
